<template>
  <div>
    <checkbox-input v-if="showRolledUpCheckbox" :checkbox-id="`${entityName}-partnership-opt-in-all`"
                    :initial-value="rolledUpValue" :input-name="`${entityName}[partnership_opt_in_all]`"
                    @change="updateRolledUpValue">
      <template #label>
        <span>
          {{ rolledUpLabel }}
        </span>
        <link-button class="unroll-button btn-sm py-0" @click="unrollCheckboxes">
          {{ i18n.t('buttons.see_all') }}
        </link-button>
      </template>
    </checkbox-input>

    <partnership-opt-in-input v-for="partnership of partnershipsToIncludeSeparately" :key="partnership.partnershipId"
                              :entity-name="entityName" :partnership-id="partnership.partnershipId"
                              :initial-value="value[partnership.partnershipId]"
                              :display-opt-in="partnership.displayOptIn" :label="partnership.label"
                              :join-partnership-default="partnership.joinPartnershipDefault"
                              :join-list-suppressed="joinListSuppressed"
                              @changed="(newValue) => updateValueForPartnership(partnership.partnershipId, newValue)" />

    <validation-errors v-if="showValidationError" :errors="validationErrors" />
  </div>
</template>

<script>
import CheckboxInput from './checkbox-input'
import LinkButton from '@/components/buttons/link-button'
import PartnershipOptInInput from './partnership-opt-in-input'
import ValidationErrors from './validation-errors'

export default {
  components: { CheckboxInput, LinkButton, PartnershipOptInInput, ValidationErrors },

  props: {
    entityName: {
      type: String,
      required: true
    },
    initialValue: {
      type: Object,
      required: false,
      default: () => ({})
    },
    joinListSuppressed: {
      type: Boolean,
      required: true
    },
    partnerships: {
      type: Array,
      required: true
    },
    rolledUpDefaultValue: {
      type: Boolean,
      default: false
    },
    rolledUpLabel: {
      type: String,
      required: false
    },
    rollUpCheckboxes: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Array,
      required: false
    }
  },

  emits: ['changed'],

  data() {
    // We should only show the rolled-up checkbox if there are at least two partnerships for which we
    // would otherwise be displaying checkboxes.
    const showRolledUpCheckbox = this.rollUpCheckboxes &&
                                 !this.joinListSuppressed &&
                                 this.partnerships.filter((p) => p.displayOptIn).length > 1

    return {
      rolledUpValue: this.rolledUpDefaultValue,
      showRolledUpCheckbox,
      value: this.initialValue || {}
    }
  },

  computed: {
    partnershipsToIncludeSeparately() {
      if (this.showRolledUpCheckbox) {
        // We're rolling up checkboxes, so only separately render partnerships that don't have a checkbox
        return this.partnerships.filter((p) => !p.displayOptIn)
      } else {
        // We're not rolling up checkboxes, so render all partnerships separately
        return this.partnerships
      }
    },
    partnershipsWithVisibleOptIns() {
      return this.partnerships.filter((p) => p.displayOptIn)
    },
    showValidationError() {
      return this.validationErrors && this.validationErrors.length > 0
    }
  },

  mounted() {
    // If we're rolling up checkboxes, ensure the starting values of everything are set correctly
    if (this.showRolledUpCheckbox) {
      this.updateRolledUpValue(this.rolledUpValue)
    }
  },

  methods: {
    unrollCheckboxes() {
      this.showRolledUpCheckbox = false
    },
    updateRolledUpValue(newValue) {
      this.rolledUpValue = newValue
      this.partnershipsWithVisibleOptIns.map((p) =>
        this.value[p.partnershipId] = newValue
      )
      this.$emit('changed', this.value)
    },
    updateValueForPartnership(partnershipId, newValue) {
      this.value[partnershipId] = newValue
      this.$emit('changed', this.value)
    }
  }
}
</script>

<style lang="scss" scoped>
.unroll-button {
  font: inherit;
  border: none;
  vertical-align: baseline;
}
</style>
